import { MutationTree } from 'vuex';
import { IPreviewToken } from '@/dealroom/document/types/preview-token';
import { IReplaceTempBlock } from '@/dealroom/types/replace-temp-block';
import { IDealroomState } from '@/dealroom/store/dealroom.store';
import { IContentLinkBlock } from '@/dealroom/content-link/types/content-link';
import { IDocument } from '@/types/Document';
import { IDocumentPage } from '@/types/document-page';
import { IRecipient } from '@/types/Recipient';
import { ITag } from '@/types/Tag';
import { IUser } from '@/types/User';
import { IDocumentAttachment } from '@/types/document-attachment';
import { IWordMerge } from '@/dealroom/types/word-merge';

export const mutationSetLoading = 'setLoading';
export const mutationSetLoaded = 'setLoaded';

export const mutationSetDocument = 'setDocument';
export const mutationSetDocumentPages = 'setDocumentPages';
export const mutationUpdateDocumentPage = 'updateDocumentPage';

export const mutationReplaceTempBlock = 'replaceTempBlock';
export const mutationRemoveTempBlock = 'removeTempBlock';

export const mutationShowEditDocumentPage = 'showEditDocumentPage';
export const mutationSetPageLoading = 'setPageLoading';

export const mutationSetContentLink = 'setContentLink';

export const mutationSetState = 'setState';

export const mutationAddDocumentTag = 'addDocumentTag';
export const mutationRemoveDocumentTag = 'removeDocumentTag';

export const mutationSetCurrentDocumentPage = 'setCurrentDocumentPage';
export const mutationSetSelectedUser = 'setSelectedUser';
export const mutationSetSelectedRecipient = 'setSelectedRecipient';

export const mutationSetRecipients = 'setRecipients';
export const mutationAddRecipient = 'addRecipient';
export const mutationUpdateRecipient = 'updateRecipient';
export const mutationUpdateDocumentAttachment = 'updateDocumentAttachment';

export const mutationUpdateDocumentUser = 'updateDocumentUser';

export const mutationSetWordMerge = 'setWordMerge';

export const mutationSetPreviewToken = 'setPreviewToken';
export const mutationShowDocumentSettings = 'showDocumentSettings';

export const mutationSetCreateDocumentId = 'setCreateDocumentId';
export const mutationSetSealValidationSuccess = 'setSealValidationSuccess';
export const mutationSetSidebarCollapsed = 'setSidebarCollapsed';

export const mutations: MutationTree<IDealroomState> = {
  [mutationSetLoading](state: IDealroomState, loading: boolean) {
    state.loading = loading;
  },
  [mutationSetLoaded](state: IDealroomState, loaded: boolean) {
    state.loaded = loaded;
  },
  [mutationShowDocumentSettings](state: IDealroomState, show: boolean) {
    state.showDocumentSettings = show;
  },
  [mutationSetDocument](state: IDealroomState, document: IDocument) {
    state.document = document;
  },
  [mutationSetDocumentPages](state: IDealroomState, documentPages: IDocumentPage[]) {
    state.document.pages = documentPages;
  },
  [mutationUpdateDocumentPage](state: IDealroomState, documentPage: IDocumentPage) {
    state.document.pages = state.document.pages.map((statePage: IDocumentPage) =>
      statePage.id === documentPage.id ? documentPage : statePage
    );
  },
  [mutationReplaceTempBlock](state: IDealroomState, { tempId, documentPage }: IReplaceTempBlock) {
    state.document.pages = state.document.pages.map((statePage: IDocumentPage) =>
      statePage.id === tempId ? documentPage : statePage
    );
  },
  [mutationRemoveTempBlock](state: IDealroomState, tempId: string) {
    state.document.pages = state.document.pages.filter(
      (statePage: IDocumentPage) => statePage.id !== tempId
    );
  },
  [mutationShowEditDocumentPage](
    state: IDealroomState,
    { isInEditMode, pageId }: { isInEditMode: boolean; pageId: string }
  ) {
    state.document.pages = state.document.pages.map((statePage: IDocumentPage) =>
      statePage.id === pageId
        ? { ...statePage, isInEditMode }
        : { ...statePage, isInEditMode: false }
    );
  },
  [mutationSetPageLoading](
    state: IDealroomState,
    { loading, pageId }: { loading: boolean; pageId: string }
  ) {
    state.document.pages = state.document.pages.map((statePage: IDocumentPage) =>
      statePage.id === pageId ? { ...statePage, loading } : statePage
    );
  },
  [mutationSetContentLink](
    state: IDealroomState,
    { pageId, contentLinkBlock }: { pageId: string; contentLinkBlock: IContentLinkBlock }
  ) {
    const page = state.document.pages.find((page: IDocumentPage) => page.id === pageId);
    if (page) {
      page.contentLinkBlock = contentLinkBlock;
    }
  },
  [mutationSetState](state: IDealroomState, newState: IDealroomState) {
    state.loading = newState.loading;
    state.loaded = newState.loaded;
    state.document = newState.document;
    state.createDocumentId = newState.createDocumentId;
    state.currentDocumentPage = newState.currentDocumentPage;
    state.selectedUser = newState.selectedUser;
    state.selectedRecipient = newState.selectedRecipient;
    state.wordMerge = newState.wordMerge;
    state.sealValidationSuccess = newState.sealValidationSuccess;
  },
  [mutationAddDocumentTag](state: IDealroomState, newTag: ITag) {
    state.document.tags = state.document.tags ? [...state.document.tags, newTag] : [newTag];
  },
  [mutationRemoveDocumentTag](state: IDealroomState, tagId: string) {
    state.document.tags = state.document.tags.filter((tag: ITag) => tag.id !== tagId);
  },
  [mutationSetCurrentDocumentPage](state: IDealroomState, documentPage: IDocumentPage) {
    state.currentDocumentPage = documentPage;
  },
  [mutationSetSelectedUser](state: IDealroomState, user: IUser) {
    state.selectedUser = user;
  },
  [mutationSetSelectedRecipient](
    state: IDealroomState,
    selected: { id: string; connectFirstRecipient: boolean }
  ) {
    state.selectedRecipient = selected;
  },
  [mutationAddRecipient](state: IDealroomState, recipient: IRecipient) {
    state.document.recipients = [...state.document.recipients, recipient];
  },
  [mutationUpdateRecipient](state: IDealroomState, updatedRecipient: IRecipient) {
    state.document.recipients =
      !!state.document.recipients.length &&
      !!state.document.recipients.some(
        (stateRecipient: IRecipient) => stateRecipient.id === updatedRecipient.id
      )
        ? state.document.recipients.map((stateRecipient: IRecipient) =>
            stateRecipient.id === updatedRecipient.id ? updatedRecipient : stateRecipient
          )
        : [...state.document.recipients, updatedRecipient];
  },
  [mutationUpdateDocumentAttachment](
    state: IDealroomState,
    documentAttachments: IDocumentAttachment[]
  ) {
    state.document.documentAttachments = documentAttachments;
  },
  [mutationSetRecipients](state: IDealroomState, recipients: IRecipient[]) {
    state.document.recipients = recipients;
  },
  [mutationUpdateDocumentUser](state: IDealroomState, user: IUser) {
    state.document.user = user;
  },
  [mutationSetWordMerge](state: IDealroomState, payload: IWordMerge) {
    state.wordMerge = payload;
  },
  [mutationSetPreviewToken](state: IDealroomState, previewToken: IPreviewToken) {
    state.previewToken = previewToken;
  },
  [mutationSetCreateDocumentId](state: IDealroomState, documentId: string) {
    state.createDocumentId = documentId;
  },
  [mutationSetSealValidationSuccess](state: IDealroomState, sealValidationSuccess: boolean) {
    state.sealValidationSuccess = sealValidationSuccess;
  },
  [mutationSetSidebarCollapsed](state: IDealroomState, sidebarCollapsed: boolean) {
    state.sidebarCollapsed = sidebarCollapsed;
  },
};
